import { type CallKpiRange, type CallListFilter, CrmFieldType, CrmObject, CrmTimelineKind } from "../generated/graphql";

// Routes, labels and formatting of the read-only Customer Service CRM: the
// Salesforce records the backend copies into its crm schema.

export const CRM_ROUTE = "/crm";
export const CRM_RECORD_ROUTE = `${CRM_ROUTE}/:objectSlug/:salesforceId`;
export const CRM_DUPLICATES_ROUTE = `${CRM_ROUTE}/duplikate`;
export const CRM_CALLS_ROUTE = `${CRM_ROUTE}/anrufe`;

// A Salesforce record id: 15 characters, or 18 with the case-safe suffix.
export const SALESFORCE_ID_PATTERN = /^[a-zA-Z0-9]{15}(?:[a-zA-Z0-9]{3})?$/;

export const CRM_OBJECT_SLUGS: Record<CrmObject, string> = {
  [CrmObject.Account]: "accounts",
  [CrmObject.Opportunity]: "opportunities",
  [CrmObject.Contact]: "contacts",
  [CrmObject.Lead]: "leads",
  [CrmObject.Case]: "cases",
  [CrmObject.Contract]: "contracts",
  [CrmObject.ContractItem]: "contract-items",
  [CrmObject.Order]: "orders",
  [CrmObject.LeadProduct]: "lead-products",
  [CrmObject.Campaign]: "campaigns",
  [CrmObject.CampaignMember]: "campaign-members",
};

// Menu order: the Service Console starts with cases, then the customers, then
// what they have.
export const CRM_MENU_OBJECTS: CrmObject[] = [
  CrmObject.Case,
  CrmObject.Account,
  CrmObject.Opportunity,
  CrmObject.Contact,
  CrmObject.Lead,
  CrmObject.ContractItem,
  CrmObject.Contract,
  CrmObject.Order,
];

export const crmObjectFromSlug = (slug: string | undefined): CrmObject | null =>
  (Object.entries(CRM_OBJECT_SLUGS).find(([, candidate]) => candidate === slug)?.[0] as CrmObject | undefined) ?? null;

// Narrows a list to a field's value, or no value when value is null; or to a
// range of calendar months in Berlin, as YYYY-MM.
export type CrmListFilter = { key: string; value?: string | null; fromMonth?: string; toMonth?: string };

// In the URL a filter is f.<key>=<value>, empty for no value, or
// m.<key>=<month> and m.<key>=<from>..<to>.
const VALUE_FILTER_PREFIX = "f.";
const MONTH_FILTER_PREFIX = "m.";
const MONTH_RANGE_SEPARATOR = "..";

export const crmFilterParams = (filters: CrmListFilter[]): [string, string][] =>
  filters.map(filter =>
    filter.fromMonth
      ? [
          `${MONTH_FILTER_PREFIX}${filter.key}`,
          filter.toMonth && filter.toMonth !== filter.fromMonth
            ? `${filter.fromMonth}${MONTH_RANGE_SEPARATOR}${filter.toMonth}`
            : filter.fromMonth,
        ]
      : [`${VALUE_FILTER_PREFIX}${filter.key}`, filter.value ?? ""]
  );

const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

// A hand-edited link may repeat a filter or garble a month: the first of each
// filter counts, and a filter with a malformed month is left out.
export const readCrmFilters = (params: URLSearchParams): CrmListFilter[] => {
  const seen = new Set<string>();
  return Array.from(params.entries()).flatMap(([name, value]): CrmListFilter[] => {
    if (seen.has(name)) return [];
    seen.add(name);
    if (name.startsWith(VALUE_FILTER_PREFIX)) {
      return [{ key: name.slice(VALUE_FILTER_PREFIX.length), value: value || null }];
    }
    if (name.startsWith(MONTH_FILTER_PREFIX)) {
      const [fromMonth, toMonth = fromMonth] = value.split(MONTH_RANGE_SEPARATOR);
      return MONTH_PATTERN.test(fromMonth) && MONTH_PATTERN.test(toMonth) && toMonth >= fromMonth
        ? [{ key: name.slice(MONTH_FILTER_PREFIX.length), fromMonth, toMonth }]
        : [];
    }
    return [];
  });
};

export const crmListPath = (object: CrmObject, listView?: string, filters: CrmListFilter[] = []): string => {
  const params = new URLSearchParams(listView ? { view: listView } : {});
  crmFilterParams(filters).forEach(([name, value]) => params.append(name, value));
  const query = params.toString();
  return `${CRM_ROUTE}/${CRM_OBJECT_SLUGS[object]}${query ? `?${query}` : ""}`;
};

// The calls behind a telephony KPI of the overview; agentKey narrows them to one agent.
export const crmCallsPath = (range: CallKpiRange, filter: CallListFilter, agentKey?: string): string =>
  `${CRM_CALLS_ROUTE}?${new URLSearchParams({ range, filter, ...(agentKey ? { agent: agentKey } : {}) }).toString()}`;

export const crmRecordPath = (object: CrmObject, salesforceId: string): string =>
  `${CRM_ROUTE}/${CRM_OBJECT_SLUGS[object]}/${encodeURIComponent(salesforceId)}`;

export const crmRelatedListPath = (parentObject: CrmObject, parentId: string, key: string, object: CrmObject): string =>
  `${CRM_ROUTE}/${CRM_OBJECT_SLUGS[object]}?${new URLSearchParams({
    parent: CRM_OBJECT_SLUGS[parentObject],
    parentId,
    related: key,
  }).toString()}`;

// The related list a list URL asks for: all three params, or none.
export type CrmRelatedParams = { object: CrmObject; salesforceId: string; key: string } | null;

export const readCrmRelated = (params: URLSearchParams): CrmRelatedParams => {
  const object = crmObjectFromSlug(params.get("parent") ?? undefined);
  const salesforceId = params.get("parentId");
  const key = params.get("related");
  return object && salesforceId && key ? { object, salesforceId, key } : null;
};

// Salesforce Lightning Design System colours of the standard object icons.
export const CRM_OBJECT_COLORS: Record<CrmObject, string> = {
  [CrmObject.Account]: "#7f8de1",
  [CrmObject.Opportunity]: "#6ec06e",
  [CrmObject.Contact]: "#a094ed",
  [CrmObject.Lead]: "#f88962",
  [CrmObject.Case]: "#f2cf5b",
  [CrmObject.Contract]: "#6ec06e",
  [CrmObject.ContractItem]: "#b781d3",
  [CrmObject.Order]: "#769ed9",
  // Lead products become the products of the opportunity.
  [CrmObject.LeadProduct]: "#fcb95b",
  [CrmObject.Campaign]: "#f49756",
  [CrmObject.CampaignMember]: "#f49756",
};

// Campaigns and their members are Marketing's: opening them needs
// marketing.read besides the CRM, as the backend guard requires.
export const isMarketingObject = (object: CrmObject | null | undefined): boolean =>
  object === CrmObject.Campaign || object === CrmObject.CampaignMember;

// Only the campaign memberships on a lead's or contact's page follow the CRM.
export const crmListNeedsMarketing = (object: CrmObject, related: CrmRelatedParams): boolean => {
  const personMemberships =
    object === CrmObject.CampaignMember &&
    (related?.object === CrmObject.Lead || related?.object === CrmObject.Contact);
  return isMarketingObject(related?.object) || (isMarketingObject(object) && !personMemberships);
};

export const CRM_TIMELINE_COLORS: Record<CrmTimelineKind, string> = {
  [CrmTimelineKind.Email]: "#95aec5",
  [CrmTimelineKind.Call]: "#48c3cc",
  [CrmTimelineKind.Comment]: "#62b7ed",
  [CrmTimelineKind.StatusChange]: "#8199af",
  [CrmTimelineKind.CaseOpened]: "#f2cf5b",
};

export const CRM_COLORS = {
  pageBackground: "#f3f3f3",
  cardBackground: "#ffffff",
  cardBorder: "#dddbda",
  cardShadow: "0 2px 2px 0 rgba(0,0,0,0.1)",
  headerBackground: "#f3f3f3",
  tableHeader: "#fafaf9",
  rowHover: "#f3f2f2",
  text: "#080707",
  textWeak: "#3e3e3c",
  label: "#706e6b",
  link: "#0176d3",
  brand: "#0176d3",
  pathComplete: "#3ba755",
  pathCurrent: "#014486",
  pathIncomplete: "#ecebea",
  pathIncompleteText: "#080707",
  timelineLine: "#c9c7c5",
  warning: "#fe9339",
  success: "#2e844a",
  error: "#ba0517",
  iconForeground: "#ffffff",
  recordedBadge: "#e0f2f4",
} as const;

// A link in a CRM table or field, as Salesforce shows it.
export const CRM_LINK_SX = {
  color: CRM_COLORS.link,
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
} as const;

const dateFormat = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeZone: "Europe/Berlin" });
const dateTimeFormat = new Intl.DateTimeFormat("de-DE", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Europe/Berlin",
});
const monthFormat = new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric", timeZone: "Europe/Berlin" });
const numberFormat = new Intl.NumberFormat("de-DE");
const percentFormat = new Intl.NumberFormat("de-DE", { style: "percent", maximumFractionDigits: 1 });
const currencyFormat = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

// Salesforce dates arrive as yyyy-mm-dd; read them as UTC so Berlin cannot shift the day.
export const formatCrmDate = (value: string): string => dateFormat.format(new Date(`${value}T00:00:00Z`));
export const formatCrmDateTime = (value: string): string => dateTimeFormat.format(new Date(value));
// The Berlin day of a moment.
export const formatCrmDayOf = (value: string): string => dateFormat.format(new Date(value));
export const formatCrmMonth = (value: string): string => monthFormat.format(new Date(value));
// A YYYY-MM month: noon UTC is the same day in Berlin.
export const formatCrmMonthKey = (month: string): string => formatCrmMonth(`${month}-01T12:00:00Z`);
// An amount in euros: 1234.5 is 1.234,50 €.
export const formatCrmCurrency = (value: string | number): string => currencyFormat.format(Number(value));
export const formatCrmCount = (value: number): string => numberFormat.format(value);
// A share between 0 and 1 as a percentage with at most one decimal: 0.875 is 87,5 %.
export const formatCrmPercent = (share: number): string => percentFormat.format(share);

// The text of a value; multi-select picklists and booleans render as chips and
// checkboxes instead.
export const formatCrmValue = (type: CrmFieldType, value: string | null | undefined): string => {
  if (value === null || value === undefined || value === "") return "";
  switch (type) {
    case CrmFieldType.Date:
      return formatCrmDate(value);
    case CrmFieldType.Datetime:
      return formatCrmDateTime(value);
    case CrmFieldType.Number:
      return numberFormat.format(Number(value));
    case CrmFieldType.Currency:
      return currencyFormat.format(Number(value));
    default:
      return value;
  }
};

export const splitMultiPicklist = (value: string | null | undefined): string[] =>
  (value ?? "")
    .split(";")
    .map(entry => entry.trim())
    .filter(Boolean);

// Minutes and seconds, like the Natterbox call log: 4:05.
export const formatCallDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return "";
  const whole = Math.round(seconds);
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, "0")}`;
};

// The badges of a CONSENT value: codes separated by semicolons; a bounce
// carries its time after an equals sign and its reason after a bar. Unknown
// codes are left out.
const CONSENT_CODES = ["NO_EMAIL", "NO_CALLS", "NO_NEWSLETTER", "EMAIL_BOUNCED"] as const;
export type CrmConsentBadge = {
  code: (typeof CONSENT_CODES)[number];
  since: string | null;
  reason: string | null;
};

const isConsentCode = (code: string): code is CrmConsentBadge["code"] =>
  (CONSENT_CODES as readonly string[]).includes(code);

export const parseCrmConsent = (value: string | null | undefined): CrmConsentBadge[] =>
  (value ?? "").split(";").flatMap(entry => {
    const [code, detail = ""] = entry.trim().split(/=(.*)/s);
    const [since, reason = ""] = detail.split(/\|(.*)/s);
    return isConsentCode(code) ? [{ code, since: since || null, reason: reason || null }] : [];
  });

// A number in international form, the only form the portal phone dials unchanged.
export const isE164Number = (value: string): boolean => /^\+[1-9][0-9]{6,14}$/.test(value);

export const telephoneHref = (value: string): string => `tel:${value.replace(/[^\d+]/g, "")}`;

// A German number in international form (+49…) as it is usually stored, so a CRM search finds it
// whatever its separators; any other number stays as it is. Display-only prefill: the backend
// search matches both forms.
export const nationalPhoneForm = (value: string): string => value.replace(/^(\+|00)49/, "0");

// Records the agent opened, newest first, like the Salesforce "Recently Viewed"
// list. Kept in this browser until the session ends.
export type CrmRecentRecord = { object: CrmObject; salesforceId: string; title: string };

export const CRM_RECENT_RECORDS_KEY = "crm.recentRecords";
const MAX_RECENT_RECORDS = 8;

const isRecentRecord = (value: unknown): value is CrmRecentRecord =>
  typeof value === "object" &&
  value !== null &&
  Object.values(CrmObject).includes((value as CrmRecentRecord).object) &&
  typeof (value as CrmRecentRecord).salesforceId === "string" &&
  typeof (value as CrmRecentRecord).title === "string";

export const readRecentCrmRecords = (storage: Pick<Storage, "getItem"> = window.localStorage): CrmRecentRecord[] => {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(CRM_RECENT_RECORDS_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter(isRecentRecord) : [];
  } catch {
    return [];
  }
};

export const rememberCrmRecord = (
  record: CrmRecentRecord,
  storage: Pick<Storage, "getItem" | "setItem"> = window.localStorage
): CrmRecentRecord[] => {
  const records = [
    record,
    ...readRecentCrmRecords(storage).filter(
      entry => entry.object !== record.object || entry.salesforceId !== record.salesforceId
    ),
  ].slice(0, MAX_RECENT_RECORDS);
  try {
    storage.setItem(CRM_RECENT_RECORDS_KEY, JSON.stringify(records));
  } catch {
    // A full or blocked storage only loses the list.
  }
  return records;
};
